import React, { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { AxisName } from "../../../../../../../../../models/warehouse.map";
import { MapCell } from "../../../../../../../../../models/warehouse.mapcell";
import { MatrixLengths, OverrideName } from "../../../../../../../../../util/buildingMapCells";
import { MapStatus } from "../../../../../../../../../util/enums";
import { PreviewStatus } from "../../../../../../../Moving/Movement/components/Map/PreviewStatus";
import {
  MapRow,
  MapSquare,
  BlankSquare,
  MapInput,
  TopViewText,
  MapWrapper,
  MapCellsContainer,
  CellButton,
} from "./styles";

interface Props {
  matrixValues: OverrideName[];
  overrideNames: OverrideName[];
  setOverrideNames: (overrideNames: OverrideName[]) => void;
  matrixLengths: MatrixLengths;
  isViewMode?: boolean;
  isCreate?: boolean;
  setHasOverride?: (value: boolean) => void;
  handleClickMapCell?: (mapCell: MapCell) => void;
  mapCells?: MapCell[];
  setHasChangedMapProperty: React.Dispatch<React.SetStateAction<boolean>>;
}

export const WarehouseMapComponent = ({
  matrixValues,
  matrixLengths,
  overrideNames,
  setOverrideNames,
  isViewMode = false,
  setHasOverride,
  isCreate = true,
  handleClickMapCell,
  mapCells,
  setHasChangedMapProperty,
}: Props) => {
  const { t: translate } = useTranslation();
  const { rowsLength, columnsLength, levelsLength } = matrixLengths;

  useEffect(() => {
    setOverrideNames(matrixValues);
  }, [matrixValues]);

  const getSquareColor = (cellStatuses: string[]) => {
    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.EMPTY)) {
      return MapStatus.EMPTY;
    }

    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.INACTIVE)) {
      return MapStatus.INACTIVE;
    }

    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.LOADED)) {
      return MapStatus.LOADED;
    }

    return MapStatus.MIXED;
  };

  const renderSquareToRowAndColumn = (indexRowButton: number, indexColumnButton: number) => {
    const mapCellsStatuses: string[] = [];

    for (let level = 0; level < levelsLength; level += 1) {
      const mapCell = mapCells?.find(
        (mapCell) =>
          mapCell?.indexLevel === level &&
          mapCell?.indexRow === indexRowButton &&
          mapCell?.indexColumn === indexColumnButton
      );

      if (!mapCell?.isActive) {
        mapCellsStatuses.push(MapStatus.INACTIVE);
      } else {
        const mapCellStatus =
          (mapCell?.storageBoxes && mapCell.storageBoxes.length > 0) ||
          (mapCell?.itemsJobs && mapCell?.itemsJobs?.length > 0)
            ? MapStatus.LOADED
            : MapStatus.EMPTY;

        mapCellsStatuses.push(mapCellStatus);
      }
    }

    const mapCell = mapCells?.filter(
      (mapCell) =>
        mapCell?.indexLevel === 0 &&
        mapCell?.indexRow === indexRowButton &&
        mapCell?.indexColumn === indexColumnButton
    )?.[0];

    return (
      <MapSquare status={getSquareColor(mapCellsStatuses)}>
        <CellButton
          onClick={() => {
            if (handleClickMapCell && mapCell) handleClickMapCell(mapCell);
          }}
        />
      </MapSquare>
    );
  };

  const inputBlank = <BlankSquare />;

  const renderInput = (matrixValue: OverrideName) => {
    return (
      <MapInput
        value={matrixValue?.newName}
        type={"text"}
        isViewMode={isViewMode}
        disabled={isViewMode}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const overrides = overrideNames?.map((override) => {
            if (
              override?.axisName === matrixValue?.axisName &&
              override?.index === matrixValue?.index
            ) {
              return {
                axisName: override?.axisName,
                index: override?.index,
                newName: event.target.value,
              };
            }

            return override;
          });

          if (setHasChangedMapProperty) setHasChangedMapProperty(true);
          if (setHasOverride) setHasOverride(true);

          setOverrideNames(overrides);
        }}
      />
    );
  };

  const rowsNames = overrideNames?.filter((matrix) => matrix.axisName === AxisName.ROW);
  const columnsName = overrideNames?.filter((matrix) => matrix.axisName === AxisName.COLUMN);

  const buildMapComponent = () =>
    useMemo(() => {
      if (rowsLength > 0 && columnsLength > 0 && levelsLength > 0) {
        const gridMap = [];

        for (let row = 0; row < rowsLength; row += 1) {
          const currentRow = [];

          for (let column = 0; column < columnsLength; column += 1) {
            if (column === 0) {
              currentRow.push(renderInput(rowsNames[row]));
            }
            currentRow.push(renderSquareToRowAndColumn(row, column));
          }

          gridMap.push(currentRow);
        }

        const currentRow = [];

        for (let column = 0; column < columnsLength; column += 1) {
          if (column === 0) {
            currentRow.push(inputBlank);
          }
          currentRow.push(renderInput(columnsName[column]));
        }

        gridMap.push(currentRow);

        return (
          <>
            {!isCreate && <PreviewStatus displayPreviewTitle={false} />}
            <MapWrapper item>
              <TopViewText>{translate("topView")}</TopViewText>

              <MapCellsContainer item xs={12} isCreate={isCreate}>
                {gridMap.map((row, index) => {
                  return (
                    <MapRow key={index}>
                      {row.map((mapElement: React.ReactNode) => {
                        return mapElement;
                      })}
                    </MapRow>
                  );
                })}
              </MapCellsContainer>
            </MapWrapper>
          </>
        );
      }

      return null;
    }, [rowsLength, columnsLength, levelsLength, columnsName, rowsNames]);

  return buildMapComponent();
};
